/**
 * Manages the scroll history for search and collection pages for returning the user to the previous scroll position
 */

interface HistoryState {
  from?: string
  from_page?: number
  product?: string
  scrollPosition?: number
}

interface SearchResult {
  page?: number
  handle: string
}

class ScrollHistoryManager {
  from = window.history.state?.from || ''
  fromPage = window.history.state?.from_page || false
  productHandle = window.history.state?.product || ''

  constructor() {
    this.initEventListeners()
  }

  get scrollY(): number {
    return window.scrollY
  }

  get scrollPosition(): number | undefined {
    return window.history.state?.scrollPosition
  }

  /**
   * Replaces the history state on page hide to store the scroll position
   */
  replaceStateOnPageHide = (e: PageTransitionEvent): void => {
    if (e.persisted) {
      this.setHistoryState(new URLSearchParams(window.location.search))
    }
  }

  /**
   * Scrolls to the previous scroll position on page show
   */
  scrollYOnPageShow = (e: PageTransitionEvent): void => {
    if (e.persisted) {
      if (this.scrollPosition !== undefined) {
        window.scrollTo({ top: this.scrollPosition, behavior: 'instant' })
      }
      this.clearScrollHistory()
    }
  }

  /**
   * Clears the history state for the current page
   */
  clearScrollHistory() {
    window.history.replaceState({}, '', window.location.href)
  }

  /**
   * Updates the history state with the current page information
   * This is used to restore the page parameter for the history scroll item to restore the correct page before Athos loads the results
   */
  updateHistoryState() {
    const urlParams = new URLSearchParams(window.location.search)
    if (this.fromPage) {
      urlParams.set('page', this.fromPage.toString())
      this.setHistoryState(urlParams)
    }
  }

  /**
   * Sets the history state with the current scroll and page information
   */
  setHistoryState = (urlParams?: URLSearchParams): void => {
    const state: HistoryState = {
      scrollPosition: this.scrollY,
      from: this.from,
      from_page: this.fromPage,
      product: this.productHandle
    }
    const url = urlParams ? `${window.location.pathname}?${urlParams}` : window.location.href
    window.history.replaceState(state, '', url)
  }

  getPageType = (): 'collection' | 'search' | '' => {
    const urlParams = new URLSearchParams(window.location.search)
    const queryParam = urlParams.get('query')
    const isCollection = window.location.pathname.includes('/collections/')
    return queryParam ? 'search' : isCollection ? 'collection' : ''
  }

  getCollectionHandle = (): string | '' => {
    return window.location.pathname.split('/').pop() || ''
  }

  /**
   * Updates the history state with collection or search information
   */
  updateHistoryStateWithCollectionInfo = (
    result: SearchResult
  ): void => {
    this.fromPage = result.page || 1
    this.productHandle = result.handle
    const pageType = this.getPageType()
    const collectionHandle = this.getCollectionHandle()
    const query = new URLSearchParams(window.location.search).get('query')
    if (pageType === 'collection' && collectionHandle) {
      this.from = collectionHandle
    } else if (pageType === 'search' && query) {
      this.from = `search:${query}`
    }
    this.setHistoryState(new URLSearchParams(window.location.search))
  }

  /**
   * Initializes the event listeners for the scroll history manager for persisted (BFCache) events
   */
  initEventListeners() {
    window.addEventListener('pagehide', this.replaceStateOnPageHide)
    window.addEventListener('pageshow', this.scrollYOnPageShow)
  }
}

export const scrollHistoryManager = new ScrollHistoryManager()
